/**
 * The state behind the "živě" indicator in the header.
 *
 * The indicator used to be a green dot with a CSS pulse animation and no
 * input at all. It stayed green with the API stopped, and it stayed green
 * while the numbers underneath it were a minute stale — which is the exact
 * situation where a dispatcher most needs to be told not to trust the screen.
 * An indicator that cannot say "no" is decoration.
 *
 * This reads the React Query cache, which is the only thing in the client
 * that actually knows: what is in flight, what failed, and how old the
 * freshest answer is.
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export type LiveState = 'live' | 'syncing' | 'stale' | 'down' | 'idle';

export interface Live {
  state: LiveState;
  /** Short header text: 'živě', 'aktualizuji', '2 min', 'nedostupné'. */
  label: string;
  /** Full sentence for the tooltip. */
  title: string;
}

/** Data older than this is no longer "live" — the grid polls every 60 s. */
const STALE_AFTER_MS = 90_000;

function describe(state: LiveState, ageMs: number | null): Live {
  switch (state) {
    case 'down':
      return {
        state,
        label: 'nedostupné',
        title: 'Server neodpovídá. Zobrazená data mohou být zastaralá.',
      };
    case 'syncing':
      return { state, label: 'aktualizuji', title: 'Načítám aktuální data ze serveru.' };
    case 'stale': {
      const mins = Math.round((ageMs ?? 0) / 60_000);
      return {
        state,
        label: mins < 2 ? 'starší data' : `${mins} min`,
        title: `Poslední úspěšná aktualizace před ${mins < 2 ? 'více než minutou' : `${mins} minutami`}.`,
      };
    }
    case 'idle':
      return { state, label: 'připraveno', title: 'Zatím se nenačítala žádná data.' };
    default:
      return { state, label: 'živě', title: 'Data jsou aktuální.' };
  }
}

/**
 * Publish a new description only when it differs from the one on screen.
 *
 * This is what keeps the header out of a feedback loop. `compute` runs on
 * every query-cache event, and the header is itself a cache subscriber: the
 * messages bell is a useQuery, so each re-render of the header touches its
 * observer, which emits a cache event, which runs `compute` again. With a
 * fresh object every time that was setState → render → event → setState,
 * for ever — thousands of synchronous updates a second, and React never got
 * round to lower-priority work. React Router navigates inside a transition,
 * so the address bar changed and the screen did not until a reload
 * (the owner, 5 October: "when I click from dashboard page to rezervace
 * page nothing happens until I refresh"). Returning the previous object
 * when nothing changed makes React bail out of the update, and the loop
 * has nothing to feed on.
 */
function same(a: Live, b: Live): boolean {
  return a.state === b.state && a.label === b.label && a.title === b.title;
}

export function useLive(): Live {
  const qc = useQueryClient();
  const [live, setLiveRaw] = useState<Live>(() => describe('idle', null));

  useEffect(() => {
    const setLive = (next: Live) => setLiveRaw((prev) => (same(prev, next) ? prev : next));
    const compute = () => {
      const all = qc.getQueryCache().getAll();
      // Only queries something is currently watching. A screen the operator
      // navigated away from ten minutes ago should not turn the dot amber.
      const active = all.filter((q) => q.getObserversCount() > 0);
      if (active.length === 0) return setLive(describe('idle', null));

      if (active.some((q) => q.state.status === 'error')) {
        return setLive(describe('down', null));
      }
      if (active.some((q) => q.state.fetchStatus === 'fetching')) {
        return setLive(describe('syncing', null));
      }
      if (active.some((q) => q.state.fetchStatus === 'paused')) {
        return setLive(describe('down', null));
      }

      const newest = Math.max(...active.map((q) => q.state.dataUpdatedAt || 0));
      if (!newest) return setLive(describe('idle', null));

      const age = Date.now() - newest;
      setLive(describe(age > STALE_AFTER_MS ? 'stale' : 'live', age));
    };

    compute();
    const unsubscribe = qc.getQueryCache().subscribe(compute);
    // Age is a function of wall-clock time, not of cache events, so the
    // transition from live to stale needs its own heartbeat.
    const timer = window.setInterval(compute, 10_000);
    return () => {
      unsubscribe();
      window.clearInterval(timer);
    };
  }, [qc]);

  return live;
}
